import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useTeamList, useCreateTeam, useUpdateTeam } from '../../api/teams';
import { useEngineerList } from '../../api/engineers';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { UsersRound } from 'lucide-react';
import { CardListSkeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { TeamDto } from '../../types/api';

const SELECT_CLS = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

interface CreateForm { name: string; teamLeadId: string; }
interface EditForm   { name: string; teamLeadId: string; }

function TeamRow({ team, canEdit }: { team: TeamDto; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const { data: engineers } = useEngineerList();
  const updateTeam = useUpdateTeam(team.id);
  const { confirm, dialog } = useConfirm();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<EditForm>({
    defaultValues: { name: team.name, teamLeadId: team.teamLeadId ?? '' },
  });

  const leadName = engineers?.find((e) => e.id === team.teamLeadId)?.name;

  const onSubmit = handleSubmit((values) => {
    const body: Record<string, unknown> = { name: values.name };
    if (values.teamLeadId) { body.teamLeadId = values.teamLeadId; }
    else                   { body.clearTeamLead = true; }
    updateTeam.mutate(body, {
      onSuccess: () => { toast.success('Team updated.'); setEditing(false); },
      onError:   (e) => applyServerErrors(e, setError),
    });
  });

  const handleDeactivate = async () => {
    if (!await confirm({ title: `Deactivate "${team.name}"?`, description: 'Members will keep their existing assignments.', confirmLabel: 'Deactivate' })) return;
    updateTeam.mutate({ deactivate: true }, { onSuccess: () => toast.success('Team deactivated.') });
  };

  if (editing) {
    return (
      <form onSubmit={onSubmit} className="px-5 py-4 space-y-3">
        <div className="space-y-1.5">
          <Label>Name <span className="text-destructive">*</span></Label>
          <Input {...register('name', { required: 'Name is required' })} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Team Lead</Label>
          <select {...register('teamLeadId')} className={SELECT_CLS}>
            <option value="">None</option>
            {engineers?.filter((e) => e.isActive).map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" type="submit" loading={updateTeam.isPending}>Save</Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => { setEditing(false); reset(); }}>Cancel</Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex items-start justify-between px-5 py-4">
      {dialog}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{team.name}</span>
          {!team.isActive && <Badge label="inactive" variant="gray" />}
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {leadName ? `Lead: ${leadName}` : 'No team lead assigned'}
        </p>
      </div>
      {canEdit && (
        <div className="ml-4 flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>
          {team.isActive && (
            <Button variant="ghost" size="sm" onClick={handleDeactivate} loading={updateTeam.isPending}>Deactivate</Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamsPage() {
  const { can } = useAuth();
  const [showCreate, setShowCreate] = useState(false);

  const { data: teams, isLoading, error, refetch } = useTeamList();
  const { data: engineers } = useEngineerList();
  const createTeam = useCreateTeam();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<CreateForm>();

  const onSubmit = handleSubmit((values) => {
    createTeam.mutate(
      { name: values.name, teamLeadId: values.teamLeadId || undefined },
      {
        onSuccess: () => { toast.success('Team created.'); reset(); setShowCreate(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  if (isLoading) return <CardListSkeleton count={4} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Teams"
        actions={
          can('head_of_rd') ? (
            <Button size="sm" onClick={() => setShowCreate((v) => !v)}>New team</Button>
          ) : undefined
        }
      />

      {showCreate && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <UsersRound className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">New team</p>
          </div>
          <form onSubmit={onSubmit}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} placeholder="e.g. OMS" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Team Lead</Label>
                <select {...register('teamLeadId')} className={SELECT_CLS}>
                  <option value="">None</option>
                  {engineers?.filter((e) => e.isActive).map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={createTeam.isPending}>Create</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => { setShowCreate(false); reset(); }}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="divide-y divide-border overflow-hidden p-0">
        {teams?.length === 0 && (
          <EmptyState icon={UsersRound} title="No teams yet" description="Create a team to start assigning engineers and sprints." />
        )}
        {teams?.map((t) => (
          <TeamRow key={t.id} team={t} canEdit={can('head_of_rd')} />
        ))}
      </Card>
    </div>
  );
}
